/* eslint-disable no-mixed-operators */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { actions as slotsHistoryActions } from 'features/slotsHistory';

import { providerItems, statusItems } from './data';
import { convertFilters } from './convertFilters';

const useModel = () => {
  const dispatch = useDispatch();
  const { history, pageCount } = useSelector(state => state.slotsHistory, shallowEqual);

  const stateValues = {
    from: new Date(dayjs().add(-3, 'month').format('YYYY-MM-DDTHH:mm:ss')),
    to: new Date(dayjs().format('YYYY-MM-DDTHH:mm:ss')),
    providers: providerItems,
    statuses: statusItems,
    page: 0,
    userId: '',
  };

  const [values, setValues] = useState(stateValues);

  const [warning, setWarning] = useState(false);

  const onChangeUserId = useCallback(value => setValues(ps => ({ ...ps, userId: value })), []);

  const onChangeGameType = useCallback(
    value =>
      setValues(ps => {
        const activeType = value.find(({ active }) => active === true)?.value ?? 'slots';
        const newProviders = providerItems.filter(p => p.type === activeType || !p.type || activeType === 'all');
        return { ...ps, gameTypes: value, providers: newProviders, page: 0 };
      }),
    [],
  );
  const onChangeProvider = useCallback(value => setValues(ps => ({ ...ps, providers: value, page: 0 })), []);
  const onChangeStatus = useCallback(value => setValues(ps => ({ ...ps, statuses: value, page: 0 })), []);
  const onChangeFrom = useCallback(date => setValues(ps => ({ ...ps, from: date })), []);
  const onChangeTo = useCallback(date => setValues(ps => ({ ...ps, to: date })), []);
  const onPageChanged = useCallback(newPage => setValues(ps => ({ ...ps, page: newPage })), []);

  // если айтэмы приходят под конкретную страницу
  const items = useMemo(
    () =>
      history.map((t, index) => ({
        ...t,
        key: `${Object.values(t).join('-')}_${index}_${values.page}`,
      })),
    [history, values.page],
  );

  // если айтэмы приходят для всех страниц
  /* const items = useMemo(
    () =>
      history.slice(values.page * itemsOnPage, values.page * itemsOnPage + itemsOnPage).map((t, index) => ({
        ...t,
        key: `${Object.values(t).join('-')}_${index}_${values.page}`,
      })),
    [history, values.page],
  ); */

  const onSubmit = useCallback(
    e => {
      e.preventDefault();
      if (!values.userId) {
        setWarning(true);
        return;
      }
      dispatch(
        slotsHistoryActions.getSlotsHistory(
          convertFilters({
            from: values.from,
            to: values.to,
            providers: values.providers,
            statuses: values.statuses,
            page: values.page,
            userId: values.userId,
          }),
        ),
      );
    },
    [values.providers, values.statuses, values.from, values.to, values.page, values.userId, dispatch],
  );

  useEffect(() => {
    setValues(stateValues);
    dispatch(slotsHistoryActions.resetSlotsHistory());
  }, []);

  useEffect(() => {
    dispatch(
      slotsHistoryActions.getSlotsHistory(
        convertFilters({
          from: values.from,
          to: values.to,
          providers: values.providers,
          statuses: values.statuses,
          page: values.page,
          userId: values.userId,
        }),
      ),
    );
  }, [values.page]);

  useEffect(() => {
    // защита от сильной связанности пагинатора с остальными фильтрами
    if (values.page > values.pageCount - 1 && values.pageCount - 1 >= 0) onPageChanged(values.pageCount - 1);
  }, [values.page, values.pageCount]);

  return useMemo(
    () => ({
      inputState: {
        values,
        onChangeGameType,
        onChangeProvider,
        onChangeStatus,
        onChangeFrom,
        onChangeTo,
        onChangeUserId,
        onPageChanged,
        onSubmit,
        setWarning,
      },
      outputState: { items, pageCount, warning },
    }),
    [
      values,
      onChangeGameType,
      onChangeProvider,
      onChangeStatus,
      onChangeFrom,
      onChangeTo,
      onChangeUserId,
      onPageChanged,
      onSubmit,
      setWarning,
      items,
      warning,
      pageCount,
    ],
  );
};

export default useModel;
